import { useStaticQuery, graphql } from "gatsby";
import React from "react";
import { find, has } from "lodash";
import styled from "@emotion/styled";

import DataSheetTableButton from "~/components/buttons/DataSheetTableButton";
import { ScreenReaderText } from "~/styles/screenReaderText";

import { useTranslationContext } from "~/context/TranslationContext";
import { useLocationContext } from "~/context/LocationContext";

import trackCustomEvent from "~/utilities/trackCustomEvent";

const Wrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`;

const Button = styled.button`
    max-height: 5rem;
    min-width: 15rem;
    text-align: left;
`;

const getLangCode = (activeCode) => {
    const languageMap = new Map([
        ["en", "en-US"],
        ["zh-cn", "zh-CHS"],
        ["zh-tw", "zh-CHT"],
    ]);

    const code = languageMap.get(activeCode);

    return code ?? "en-US";
};

const trackMultiviewModalOpen = (props, location) => {
    const { dataSheet, sku } = props;

    const productLines = dataSheet?.productLines?.nodes;
    const productLine = find(productLines, (productLine) => productLine);
    const name = productLine?.name || "";

    const eventDataText = {
        action: "Multiview Modal Open",
        category: "CAD Model Downloads",
        value: sku,
        additionalTracking: {
            productLine: name,
            documentIsCad: true,
            documentIsGated: true,
            url: location.href,
        },
    };

    trackCustomEvent("gaEvent", eventDataText);
};

export const useShouldShowMultiview = () => {
    const { wp } = useStaticQuery(graphql`
        query CADModelStaticQuery {
            wp {
                optionsPage {
                    globalOptions {
                        showMultiviewCadModel
                    }
                }
            }
        }
    `);

    const globalOptions = wp?.optionsPage?.globalOptions;
    return globalOptions?.showMultiviewCadModel;
};

/**
 * Multiview Documentation - https://wolfspeed.componentsearchengine.com/multiviewDocs.php
 */
const MultiviewCADModel = (props) => {
    const { sku } = props;

    const { activeCode } = useTranslationContext();
    const { location } = useLocationContext();

    const manufacturer = "Wolfspeed";
    const partNumber = sku;
    const branding = "wolfspeed";
    const fadeBackground = 1;
    const format = "zip";
    const testing = 0;
    const logo = "";
    const lang = getLangCode(activeCode);
    const exactPnMatch = 1;
    const datasheetURL = "";
    const only3d = 0;
    const showTextLabel = 1;
    const labelColor = "000000";
    const show3dAvailability = 1;
    const url = `https://wolfspeed.componentsearchengine.com/icon.php?mna=${manufacturer}&mpn=${partNumber}&lbl=${showTextLabel}&lbc=${labelColor}&q3=${show3dAvailability}&lang=${lang}`;

    return (
        <Wrapper>
            <Button
                onClick={() => {
                    if (has(window, "loadPartDiv")) {
                        trackMultiviewModalOpen(props, location);
                        window.loadPartDiv(
                            manufacturer,
                            partNumber,
                            branding,
                            fadeBackground,
                            format,
                            testing,
                            logo,
                            lang,
                            exactPnMatch,
                            datasheetURL,
                            only3d,
                        );
                    } else {
                        console.error("loadPartDiv is not defined");
                    }
                }}
            >
                <img src={url} alt="" loading="lazy" />
                <ScreenReaderText>{`Download ${partNumber}`}</ScreenReaderText>
            </Button>
        </Wrapper>
    );
};

const CADModelButton = (props) => {
    const showMultiviewCadModel = useShouldShowMultiview();

    if (showMultiviewCadModel) {
        return <MultiviewCADModel {...props} />;
    }

    return <DataSheetTableButton icon="cad" {...props} />;
};

export default CADModelButton;
